/**
 * SchemaAgentSheet — the schema preparation agent's side panel on the case
 * schema page (PRD-0025, slices 01–02). Four states, one sheet: structured
 * intake, live progress, clarification, and proposal review with Accept / Discard.
 * Reopening the page shows
 * the same session through the current-session query, so a run survives the
 * browser closing (SC-004).
 */

import { Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ClarificationCard } from '@/components/agent/ClarificationCard'
import { SchemaAgentKickoff } from '@/components/schema/agent/SchemaAgentKickoff'
import { SchemaAgentProgress } from '@/components/schema/agent/SchemaAgentProgress'
import { SchemaAgentReview } from '@/components/schema/agent/SchemaAgentReview'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { toast } from '@/components/ui/sonner'
import type { SchemaSession, SchemaSessionStartBody } from '@/services/api/schemaSessions'
import { schemaErrorMessage } from './errors'
import {
  isSessionActive,
  useAcceptSchemaSession,
  useAnswerSchemaClarification,
  useCurrentSchemaSession,
  useDiscardSchemaSession,
  useRepairSchemaProposal,
  useSchemaSessionDocuments,
  useStartSchemaSession,
} from './hooks/use-schema-session'
import { useSchemaRunProgress, useSchemaSessionEvents } from './hooks/use-schema-session-events'

import { MAX_REFERENCE_FILES } from './intake'

export interface SchemaAgentSheetProps {
  caseId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  caseOwnsSchema: boolean
  viewingDraft: boolean
  onViewDraft: (view: boolean) => void
  /** Called with the accepted schema's id once accept lands. */
  onAccepted: (schemaId: string) => Promise<void>
  startNew?: boolean
  onStarted?: () => void
}

/** Which panel state a session is in. */
function sessionMode(
  session: SchemaSession | null | undefined
): 'kickoff' | 'progress' | 'clarification' | 'review' {
  if (!session || session.status === 'closed') return 'kickoff'
  if (session.latestRun?.status === 'awaiting_input') return 'clarification'
  if (isSessionActive(session)) return 'progress'
  if (session.latestRun?.proposal) return 'review'
  return 'kickoff'
}

export function SchemaAgentSheet({
  caseId,
  open,
  onOpenChange,
  caseOwnsSchema,
  viewingDraft,
  onViewDraft,
  onAccepted,
  startNew = false,
  onStarted,
}: SchemaAgentSheetProps) {
  const { t } = useTranslation()
  const sessionQuery = useCurrentSchemaSession(caseId)
  const { data: session } = sessionQuery
  const mode = startNew ? 'kickoff' : sessionMode(session)
  const documents = useSchemaSessionDocuments(caseId, open && mode === 'kickoff')
  const start = useStartSchemaSession(caseId)
  const answer = useAnswerSchemaClarification(caseId)
  const accept = useAcceptSchemaSession(caseId)
  const discard = useDiscardSchemaSession(caseId)
  const repair = useRepairSchemaProposal(caseId)
  const events = useSchemaSessionEvents({
    caseId,
    sessionId: session?.id,
    runId: session?.latestRun?.id,
    active: isSessionActive(session),
    agentThreadId: session?.agentThreadId,
  })

  const run = session?.latestRun ?? null
  const failed = run?.status === 'finished_error'
  const progress = useSchemaRunProgress(run, events.events)

  const handleStart = async (intake: SchemaSessionStartBody) => {
    try {
      await start.mutateAsync(intake)
      try {
        sessionStorage.removeItem(`schema-intake:${caseId}`)
      } catch {
        /* Storage is optional. */
      }
      onStarted?.()
      // Straight to the workbench: the run's transcript streams into its chat.
      onViewDraft(true)
    } catch (err) {
      toast.error(schemaErrorMessage(err, t('schemaAgent.kickoff.startError')))
    }
  }

  const handleAccept = async (name: string | undefined) => {
    if (!session) return
    try {
      const accepted = await accept.mutateAsync({
        sessionId: session.id,
        body: name ? { name } : {},
      })
      if (accepted.acceptedSchemaId) await onAccepted(accepted.acceptedSchemaId)
      toast.success(t('schemaAgent.review.accepted'))
      onViewDraft(false)
      onOpenChange(false)
    } catch (err) {
      console.error(err)
      toast.error(t('schemaAgent.review.acceptError'))
    }
  }

  const handleDiscard = async () => {
    if (!session) return
    try {
      await discard.mutateAsync(session.id)
      onViewDraft(false)
    } catch (err) {
      console.error(err)
      toast.error(t('schemaAgent.review.discardError'))
    }
  }

  const handleRepair = async (feedback: string) => {
    if (!session) return
    try {
      await repair.mutateAsync({ sessionId: session.id, feedback })
      onViewDraft(false)
    } catch (err) {
      toast.error(schemaErrorMessage(err, t('schemaAgent.review.repairError')))
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-hidden sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="size-4 text-primary" />
            {t('schemaAgent.title')}
          </SheetTitle>
          <SheetDescription>
            {mode === 'review'
              ? t('schemaAgent.review.description')
              : t('schemaAgent.description', { max: MAX_REFERENCE_FILES })}
          </SheetDescription>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4">
          {(sessionQuery.isError || documents.isError) && (
            <div
              role="alert"
              className="space-y-2 rounded-md border border-destructive/30 p-4 text-sm"
            >
              <p>
                {t(
                  sessionQuery.isError
                    ? 'schemaAgent.error.loadSession'
                    : 'schemaAgent.error.loadDocuments'
                )}
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  void sessionQuery.refetch()
                  void documents.refetch()
                }}
              >
                {t('schemaAgent.error.retry')}
              </Button>
            </div>
          )}
          {(sessionQuery.isPending || (mode === 'kickoff' && documents.isPending)) && (
            <p role="status">{t('schemaAgent.kickoff.loading')}</p>
          )}
          {mode === 'progress' && (
            <SchemaAgentProgress
              isConnected={events.isConnected}
              startedAt={run?.startedAt ?? null}
              files={progress.files}
              stepsCompleted={progress.steps}
              lastTool={progress.lastTool}
            />
          )}

          {mode === 'clarification' && run?.clarification && session && (
            <ClarificationCard
              clarification={run.clarification}
              busy={answer.isPending}
              onSubmit={async answers => {
                try {
                  await answer.mutateAsync({ sessionId: session.id, body: { answers } })
                } catch (err) {
                  toast.error(schemaErrorMessage(err, t('schemaAgent.error.answer')))
                }
              }}
            />
          )}

          {mode === 'review' && run?.proposal && session && (
            <>
              {run.errorMessage && (
                <p role="alert" className="text-sm text-destructive">
                  {run.errorMessage}
                </p>
              )}
              <SchemaAgentReview
                key={run.id}
                proposal={run.proposal}
                review={{ hasDraft: session.draft != null, caseOwnsSchema, viewingDraft }}
                actions={{
                  pending: accept.isPending
                    ? 'accept'
                    : discard.isPending
                      ? 'discard'
                      : repair.isPending
                        ? 'repair'
                        : null,
                  viewDraft: onViewDraft,
                  accept: handleAccept,
                  discard: handleDiscard,
                  repair: handleRepair,
                }}
              />
            </>
          )}

          {(mode === 'progress' || mode === 'clarification') && (
            <Button variant="ghost" disabled={discard.isPending} onClick={handleDiscard}>
              {t('schemaAgent.review.discard')}
            </Button>
          )}

          {mode === 'kickoff' &&
            !sessionQuery.isPending &&
            !sessionQuery.isError &&
            !documents.isPending &&
            !documents.isError && (
              <>
                {failed && session && (
                  <div className="flex flex-col gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                    <span>{run?.errorMessage || t('schemaAgent.failed')}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDiscard}
                      disabled={discard.isPending}
                      className="self-start border-border"
                    >
                      {t('schemaAgent.review.discard')}
                    </Button>
                  </div>
                )}
                <SchemaAgentKickoff
                  key={caseId}
                  context={{ caseId }}
                  documents={documents.data ?? []}
                  onStart={handleStart}
                  isPending={start.isPending}
                />
              </>
            )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
